import { DEFAULT_INTERACT_RADIUS, forwardFromYaw, type MapObject } from '@etown/shared';

/**
 * Welches Objekt liegt im Fadenkreuz? Ein Strahl vom Fusspunkt des Spielers in
 * Blickrichtung wird gegen die Grundflaechen der Objekte geschnitten; von den
 * getroffenen zaehlt das naechstgelegene in Reichweite.
 *
 * Ob etwas in Reichweite ist, entscheidet — wie in der Draufsicht — der
 * kuerzeste Abstand zur Objektflaeche, nicht die Laenge des Strahls. Sonst
 * waere dasselbe Objekt aus derselben Entfernung mal benutzbar und mal nicht,
 * je nachdem wie schraeg man davorsteht.
 *
 * Reine Geometrie in Tile-Koordinaten — kein Raycast der Grafikbibliothek.
 */
export function objectInCrosshair(
  objects: readonly MapObject[],
  fromX: number,
  fromY: number,
  yaw: number,
): MapObject | null {
  const dir = forwardFromYaw(yaw);
  let best: MapObject | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const obj of objects) {
    const alongRay = rayRectDistance(fromX, fromY, dir.x, dir.y, obj);
    if (alongRay === null || alongRay >= bestDistance) continue;
    const reach = obj.interactRadius || DEFAULT_INTERACT_RADIUS;
    if (distanceToRect(fromX, fromY, obj) > reach) continue;
    best = obj;
    bestDistance = alongRay;
  }
  return best;
}

/** Kuerzeste Distanz vom Punkt zur Objekt-Flaeche (Tile-Rechteck). */
export function distanceToRect(px: number, py: number, rect: { x: number; y: number; w: number; h: number }): number {
  const nearestX = Math.max(rect.x, Math.min(px, rect.x + rect.w));
  const nearestY = Math.max(rect.y, Math.min(py, rect.y + rect.h));
  return Math.hypot(px - nearestX, py - nearestY);
}

/**
 * Entfernung bis zum Eintritt in das Rechteck, oder null wenn der Strahl es
 * verfehlt. Steht der Spieler bereits darin, ist die Entfernung 0.
 */
export function rayRectDistance(
  fromX: number,
  fromY: number,
  dirX: number,
  dirY: number,
  rect: { x: number; y: number; w: number; h: number },
): number | null {
  const near = { x: rect.x, y: rect.y };
  const far = { x: rect.x + rect.w, y: rect.y + rect.h };

  // Schnittparameter je Achse (Slab-Verfahren); ein Richtungsanteil von 0
  // bedeutet: parallel zu dieser Achse
  const spanX = slab(fromX, dirX, near.x, far.x);
  const spanY = slab(fromY, dirY, near.y, far.y);
  if (!spanX || !spanY) return null;

  const entry = Math.max(spanX.enter, spanY.enter);
  const exit = Math.min(spanX.exit, spanY.exit);
  if (entry > exit || exit < 0) return null;
  return Math.max(entry, 0);
}

function slab(origin: number, direction: number, min: number, max: number): { enter: number; exit: number } | null {
  if (Math.abs(direction) < 1e-9) {
    // Parallel: nur ein Treffer, wenn der Ursprung zwischen den Kanten liegt
    return origin >= min && origin <= max
      ? { enter: Number.NEGATIVE_INFINITY, exit: Number.POSITIVE_INFINITY }
      : null;
  }
  const a = (min - origin) / direction;
  const b = (max - origin) / direction;
  return { enter: Math.min(a, b), exit: Math.max(a, b) };
}
